// ===========================================
// MODAL DEMO PAGE
// ===========================================
// Preview connection-related modals for testing/design purposes.
// Route: /modal-demo
// ===========================================

import { useState, useEffect, useCallback } from 'react';
import { ReconnectingModal } from '../components/ReconnectingModal';
import { ReconnectModal } from '../components/game/ReconnectModal';
import { ConnectionWarning } from '../components/game/ConnectionWarning';

// Card images for mock seat
import aceSpades from '../assets/cards/AS_face.svg';
import kingHearts from '../assets/cards/king-hearts.svg';

export function ModalDemoPage() {
  const [showReconnecting, setShowReconnecting] = useState(false);
  const [showRestoreAudio, setShowRestoreAudio] = useState(false);
  const [showConnectionWarning, setShowConnectionWarning] = useState(false);
  const [warningEscalated, setWarningEscalated] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Listen for online/offline events (instant detection)
  useEffect(() => {
    const handleOnline = () => {
      console.log('[ModalDemo] Browser went ONLINE');
      setIsOnline(true);
    };
    const handleOffline = () => {
      console.log('[ModalDemo] Browser went OFFLINE');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle escalation from ConnectionWarning to ReconnectingModal
  const handleEscalate = useCallback(() => {
    console.log('[ModalDemo] Connection warning escalated to full modal');
    setShowConnectionWarning(false);
    setWarningEscalated(true);
  }, []);

  // Reset escalation state
  const resetWarningDemo = () => {
    setShowConnectionWarning(false);
    setWarningEscalated(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <h1 className="text-3xl font-bold text-casino-gold-400 mb-2">Modal Demo</h1>
      <p className="text-gray-400 mb-8">Preview connection-related modals</p>

      {/* Current Status - Updates live via online/offline events */}
      <div className="bg-gray-800 rounded-lg p-4 mb-8 max-w-md">
        <h2 className="text-lg font-semibold mb-3">Current Status (Live)</h2>
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500 animate-pulse'}`} />
          <span className="text-gray-300">
            navigator.onLine: <code className={isOnline ? 'text-green-400' : 'text-red-400'}>{String(isOnline)}</code>
          </span>
          <span className="ml-auto text-xs text-gray-500">auto-updating</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Toggle Chrome DevTools &gt; Network &gt; Offline to test instant detection
        </p>
      </div>

      {/* Modal Buttons */}
      <div className="space-y-6 max-w-md">
        {/* Tiered Connection Warning (NEW) */}
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-red-400 mb-2">Tiered Connection Warning (NEW)</h2>
          <p className="text-sm text-gray-400 mb-3">
            <strong>Phase 1 (0-5s):</strong> Pulsing WiFi icon over player's seat<br />
            <strong>Phase 2 (5s+):</strong> Escalates to full blur + ReconnectingModal
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                resetWarningDemo();
                setShowConnectionWarning(true);
              }}
              className="bg-red-500 hover:bg-red-400 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
              Start Warning
            </button>
            <button
              onClick={resetWarningDemo}
              className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
              Reset
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Watch the 5s countdown, then it escalates to full modal
          </p>
        </div>

        {/* ReconnectingModal */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-2">ReconnectingModal</h2>
          <p className="text-sm text-gray-400 mb-3">
            Full-screen blur + modal. Shown after 5s of connection warning
            OR when Socket.IO disconnects.
          </p>
          <button
            onClick={() => setShowReconnecting(true)}
            className="bg-casino-gold-500 hover:bg-casino-gold-600 text-black font-bold py-2 px-4 rounded-lg transition-colors"
          >
            Show Modal
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Click outside or wait - modal will auto-hide after 3 seconds for demo
          </p>
        </div>

        {/* Restore Audio Modal */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-2">Restore Audio Modal</h2>
          <p className="text-sm text-gray-400 mb-3">
            Shown to <strong>SPECTATORS only</strong> after page refresh to restore browser audio.
            Features 10-second auto-dismiss countdown for non-intrusive UX.
          </p>
          <button
            onClick={() => setShowRestoreAudio(true)}
            className="bg-casino-gold-500 hover:bg-casino-gold-600 text-black font-bold py-2 px-4 rounded-lg transition-colors"
          >
            Show Modal
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Watch the countdown! Click YES or let it auto-dismiss.
          </p>
        </div>

        {/* Info */}
        <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-green-400 mb-2">Tiered Approach</h2>
          <ul className="text-sm text-gray-300 space-y-2">
            <li>
              <strong>0-5 seconds:</strong> Large pulsing WiFi icon over player's seat.
              Non-blocking, alerts player to check connection.
            </li>
            <li>
              <strong>5+ seconds:</strong> Full blur + ReconnectingModal.
              Blocks all UI, serious connectivity issue.
            </li>
            <li>
              <strong>~80% of disconnects</strong> resolve within 5s, so most players
              only see the brief warning.
            </li>
          </ul>
        </div>
      </div>

      {/* Connection Warning Demo - positioned in a mock "seat" area */}
      {showConnectionWarning && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-36 h-[168px] bg-green-800/50 rounded-lg border-2 border-dashed border-gray-500 flex items-center justify-center">
          {/* Mock cards in seat */}
          <div className="flex items-center justify-center">
            <img src={aceSpades} alt="Ace" className="w-[74px] h-[105px] rounded shadow-lg" />
            <img src={kingHearts} alt="King" className="w-[74px] h-[105px] rounded shadow-lg -ml-12" />
          </div>
          {/* WiFi warning overlay - fills entire seat (144x168) */}
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 9999999 }}>
            <ConnectionWarning
              show={true}
              onEscalate={handleEscalate}
            />
          </div>
          <span className="absolute -bottom-6 text-xs text-gray-400">Mock Player Seat (144x168)</span>
        </div>
      )}

      {/* Escalated modal */}
      {warningEscalated && (
        <>
          <ReconnectingModal forceShow={true} />
          <div
            className="fixed inset-0 z-[10000]"
            onClick={resetWarningDemo}
          />
          <AutoClose onClose={resetWarningDemo} delay={3000} />
        </>
      )}

      {/* Standalone ReconnectingModal demo */}
      {showReconnecting && (
        <>
          <ReconnectingModal forceShow={true} />
          <div
            className="fixed inset-0 z-[10000]"
            onClick={() => setShowReconnecting(false)}
          />
          <AutoClose onClose={() => setShowReconnecting(false)} delay={3000} />
        </>
      )}

      <ReconnectModal
        show={showRestoreAudio}
        onRestoreAudio={() => {
          console.log('[ModalDemo] User clicked YES to restore audio');
          setShowRestoreAudio(false);
        }}
        onDismiss={() => {
          console.log('[ModalDemo] Modal auto-dismissed (countdown expired)');
          setShowRestoreAudio(false);
        }}
      />
    </div>
  );
}

// Helper to auto-close modal after delay
function AutoClose({ onClose, delay }: { onClose: () => void; delay: number }) {
  useEffect(() => {
    const timer = setTimeout(onClose, delay);
    return () => clearTimeout(timer);
  }, [onClose, delay]);
  return null;
}

export default ModalDemoPage;
